import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Stack,
  Alert,
  Chip,
  Container,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
} from "@mui/material";
import {
  FolderOpen as FolderOpenIcon,
  Description as DescriptionIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const FileSelectPage: React.FC = () => {
  const navigate = useNavigate();

  const handleNext = (parentFile: File, allFiles: File[]) => {
    navigate("/traverse", {
      state: {
        parentFile,
        allFiles,
      },
    });
  };

  return <FileSelect onNext={handleNext} />;
};

export default FileSelectPage;

interface FileSelectProps {
  onNext: (parentFile: File, allFiles: File[]) => void;
}

const FileSelect: React.FC<FileSelectProps> = ({ onNext }) => {
  const [parentFile, setParentFile] = useState<File | null>(null);
  const [folderFiles, setFolderFiles] = useState<File[]>([]);
  const [htmlFiles, setHtmlFiles] = useState<File[]>([]);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [error, setError] = useState<string>("");

  const handleParentFileSelect = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type === "text/html" || file.name.endsWith(".html")) {
        setParentFile(file);
        setError("");
      } else {
        setError("Please select an HTML file");
      }
    }
  };

  const handleFolderSelect = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (files) {
      const fileArray = Array.from(files);

      // Separate HTML and image files
      const htmlFilesArray = fileArray.filter(
        (file) => file.type === "text/html" || file.name.endsWith(".html")
      );

      const imageFilesArray = fileArray.filter(
        (file) =>
          file.type.startsWith("image/") ||
          file.name.match(/\.(jpg|jpeg|png|gif|svg|webp)$/i)
      );

      if (htmlFilesArray.length === 0) {
        setError("No HTML files found in the selected folder");
      } else {
        setFolderFiles(fileArray); // Store all files
        setHtmlFiles(htmlFilesArray);
        setImageFiles(imageFilesArray);
        setError("");
      }
    }
  };

  const handleClearParent = () => {
    setParentFile(null);
  };

  const handleClearFolder = () => {
    setFolderFiles([]);
    setHtmlFiles([]);
    setImageFiles([]);
  };

  const handleNext = () => {
    if (parentFile && folderFiles.length > 0) {
      // Pass all files including images
      onNext(parentFile, folderFiles);
    }
  };

  const canProceed = parentFile && folderFiles.length > 0;

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Notion to PDF Generator
        </Typography>

        {/* Disclaimer */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Disclaimer:</strong> This service is not affiliated with,
            endorsed by, or connected to Notion Labs, Inc. in any way. All
            references to "Notion" are used solely for the purpose of describing
            compatibility and functionality, and constitute fair use under
            applicable trademark law.
          </Typography>
        </Alert>

        {/* Export Instructions */}
        <Paper sx={{ p: 2, mb: 3, backgroundColor: "#f8f9fa" }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                <InfoIcon color="primary" fontSize="small" />
                <Typography variant="subtitle1" fontWeight="medium">
                  How to export from Notion
                </Typography>
              </Stack>

              <Stack spacing={0.5}>
                <Typography variant="body2">
                  1. In Notion, click ••• → Export
                </Typography>
                <Typography variant="body2">2. Use these settings:</Typography>
                <Box sx={{ pl: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    • Format: <strong>HTML</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    • Include subpages: <strong>ON</strong>
                  </Typography>
                  <Typography variant="body2" color="error">
                    • Create folders: <strong>OFF</strong> ⚠️
                  </Typography>
                </Box>
                <Typography variant="body2">
                  3. Export and extract the ZIP file
                </Typography>
              </Stack>
            </Box>

            <Box
              component="img"
              src="/images/notion-export.png"
              alt="Notion export settings"
              sx={{
                width: 200,
                height: "auto",
                borderRadius: 1,
                boxShadow: 1,
              }}
            />
          </Stack>
        </Paper>

        <Stack spacing={3}>
          {/* Folder Selection */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                1. Select Notion export folder
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Select the folder containing your extracted Notion HTML files
                and images
              </Typography>

              {folderFiles.length === 0 ? (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<FolderOpenIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                >
                  Select Export Folder
                  <input
                    type="file"
                    hidden
                    onChange={handleFolderSelect}
                    {...({ webkitdirectory: "", directory: "" } as any)}
                  />
                </Button>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Stack direction="row" spacing={2}>
                        <Chip
                          label={`${htmlFiles.length} HTML files`}
                          color="primary"
                          variant="outlined"
                        />
                        {imageFiles.length > 0 && (
                          <Chip
                            label={`${imageFiles.length} images`}
                            color="secondary"
                            variant="outlined"
                          />
                        )}
                      </Stack>
                      <IconButton onClick={handleClearFolder} size="small">
                        <ClearIcon />
                      </IconButton>
                    </Stack>

                    {htmlFiles.length <= 10 ? (
                      <List dense sx={{ maxHeight: 200, overflow: "auto" }}>
                        {htmlFiles.map((file, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={file.name}
                              primaryTypographyProps={{ variant: "body2" }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Showing count only (too many files to list)
                      </Typography>
                    )}
                  </Stack>
                </Paper>
              )}
            </CardContent>
          </Card>

          {/* Parent File Selection */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                2. Select parent HTML file
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Choose the main HTML file that represents the root page of your
                documentation
              </Typography>

              {!parentFile ? (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<DescriptionIcon />}
                  fullWidth
                  sx={{ py: 2 }}
                  disabled={folderFiles.length === 0}
                >
                  {folderFiles.length === 0
                    ? "Select a folder first"
                    : "Select Parent HTML File"}
                  <input
                    type="file"
                    hidden
                    accept=".html,text/html"
                    onChange={handleParentFileSelect}
                  />
                </Button>
              ) : (
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <DescriptionIcon color="primary" />
                      <Typography variant="body1">{parentFile.name}</Typography>
                    </Stack>
                    <IconButton onClick={handleClearParent} size="small">
                      <ClearIcon />
                    </IconButton>
                  </Stack>
                </Paper>
              )}

              {folderFiles.length > 0 && !parentFile && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Tip: The parent file is typically the main page of your
                    Notion export
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Error Alert */}
          {error && (
            <Alert severity="error" onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed}
              size="large"
            >
              Next: Select pages for export
            </Button>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
};
